// Copyright 2018 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, observable } from "aurelia-framework";
import { AlertContent } from "controls/multi-alert/MultiAlert";
import { ScanArea, SubScanArea } from "datatypes/ScanStep";
import { Point3D } from "datatypes/SoramaWebApi";
import { URA, URAProperties } from "libraries/Math/URA";
import { RecordingStepFactory } from "libraries/Scanning/RecordingStepsFactory";
import { IWorkflowModuleLifetime } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { MotionDeviceService } from "services/MotionDevice/MotionDeviceService";
import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";

@autoinject
export class MeasurementAreaModule implements IWorkflowModuleLifetime {
    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public recordingGroupContainerId: string;

    // Scan Plan Settings
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public scanningArea: ScanArea;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public subScanArea: SubScanArea;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public channelSelection: ChannelSelection;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public measurementDistance: number;

    //#endregion

    @observable private resolution: number;

    private svgMargin = 50;
    private svgWidth = 400;
    private svgHeight = 300;
    private svgSmallMargin = 3;
    private svgMediumMargin = 10;

    @computedFrom("scanningArea", "scanningArea.width", "scanningArea.height", "uraProperties")
    private get svgArrayWidth(): number {
        const height = this.svgHeight - 2 * this.svgMargin;
        const width = this.svgWidth - 2 * this.svgMargin;

        const arrayWidth = !this.uraProperties ? 1 : this.scanningArea.width / this.uraProperties.width;
        const arrayHeight = !this.uraProperties ? 1 : this.scanningArea.height / this.uraProperties.width;

        const result = Math.min(width / Math.max(1, arrayWidth), height / Math.max(1, arrayHeight));
        return result;
    }

    @computedFrom("scanningArea")
    public get areaWidth(): number {
        return this.scanningArea.width;
    }

    public set areaWidth(val: number) {
        this.scanningArea = {
            offsetX: this.scanningArea.offsetX,
            offsetY: this.scanningArea.offsetY,
            offsetZ: this.scanningArea.offsetZ,
            height: this.scanningArea.height,
            width: val,
        };
    }

    @computedFrom("scanningArea")
    public get areaHeight(): number {
        return this.scanningArea.height;
    }

    public set areaHeight(val: number) {
        this.scanningArea = {
            offsetX: this.scanningArea.offsetX,
            offsetY: this.scanningArea.offsetY,
            offsetZ: this.scanningArea.offsetZ,
            height: val,
            width: this.scanningArea.width,
        };
    }

    @computedFrom("scanningArea")
    public get areaOffsetX(): number {
        return this.scanningArea.offsetX;
    }

    public set areaOffsetX(val: number) {
        this.scanningArea = {
            offsetX: val,
            offsetY: this.scanningArea.offsetY,
            offsetZ: this.scanningArea.offsetZ,
            height: this.scanningArea.height,
            width: this.scanningArea.width,
        };
    }

    @computedFrom("scanningArea")
    public get areaOffsetY(): number {
        return this.scanningArea.offsetY;
    }

    public set areaOffsetY(val: number) {
        this.scanningArea = {
            offsetX: this.scanningArea.offsetX,
            offsetY: val,
            offsetZ: this.scanningArea.offsetZ,
            height: this.scanningArea.height,
            width: this.scanningArea.width,
        };
    }

    @computedFrom("scanningArea")
    public get areaOffsetZ(): number {
        return this.scanningArea.offsetZ;
    }

    public set areaOffsetZ(val: number) {
        this.scanningArea = {
            offsetX: this.scanningArea.offsetX,
            offsetY: this.scanningArea.offsetY,
            offsetZ: val,
            height: this.scanningArea.height,
            width: this.scanningArea.width,
        };
    }

    private containsRecordings = false;
    private canChange = true;

    private measurementAlerts: AlertContent[] = [];

    private uraProperties: URAProperties;
    private subscriptions: Disposable[];

    constructor(
        private readonly motionService: MotionDeviceService,
        private readonly stepFactory: RecordingStepFactory,
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly bindingEngine: BindingEngine,
        private readonly recordingGroupContainerStorageService: IRecordingGroupContainerStorageService
    ) {}

    public activateModule(): void {
        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "_activeSetup")
                .subscribe(this.onSelectedSetupChanged),
        ];

        if (this.measurementAcquisitionService.activeSetup) this.onSelectedSetupChanged();

        if (this.recordingGroupContainerId) this.recordingGroupContainerIdChanged();
    }

    public deactivateModule(): void {
        while (this.subscriptions.length) this.subscriptions.pop().dispose();
    }

    private onSelectedSetupChanged = () => {
        this.canChange = this.containsRecordings || !this.measurementAcquisitionService.isAcquiring.notAvailable;

        // Hardware Check
        const idx = this.measurementAlerts.findIndex((x) => x.alertText === "No Hardware Connected");
        this.measurementAlerts.splice(idx, 1);

        if (!this.measurementAcquisitionService.activeSetup) {
            this.measurementAlerts.push({
                alertText: "No Hardware Connected",
                alertIcon: "",
                alertSubText: "Select the hardware in the 'Prepare' module",
                alertType: "alert-danger",
                secondaryIcon: "",
            });
            return;
        }

        // URA check
        this.uraProperties = URA.getUraProperties3D(
            this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => {
                return { x: x.X, y: x.Y, z: x.Z };
            })
        );

        // Only update when the values are not set by the user
        if (!this.scanningArea) {
            this.scanningArea = {
                height: this.uraProperties.width,
                width: this.uraProperties.height,
                offsetX: 0,
                offsetY: 0,
                offsetZ: 0,
            };
        }

        if (!this.subScanArea) {
            this.subScanArea = new SubScanArea();
        }

        this.resolution = this.uraProperties.spacing / this.subScanArea.numberOfHorizontalSteps;
    };

    private recordingGroupContainerIdChanged(): void {
        this.recordingGroupContainerStorageService.load(this.recordingGroupContainerId).then((x) => {
            if (x.metadata.steps) {
                this.containsRecordings = !!x.metadata.steps.find((x) => !!x.recordingId);
                this.canChange = this.containsRecordings;
            }
        });
    }

    //#region ChangeHandlers
    private resolutionChanged(): void {
        this.subScanArea = {
            numberOfHorizontalSteps: Math.max(1, this.uraProperties.spacing / this.resolution),
            numberOfVerticalSteps: Math.max(1, this.uraProperties.spacing / this.resolution),
            horizontalStep: this.resolution,
            verticalStep: this.resolution,
        };

        this.measurementAreaChanged();
    }

    private subScanAreaChanged(): void {
        if (!this.subScanArea || !this.uraProperties) return;

        this.resolution = this.uraProperties.spacing / this.subScanArea.numberOfHorizontalSteps;

        this.measurementAreaChanged();
    }

    private measurementAreaChanged(): void {
        const subScanArea = this.stepFactory.resolutionToSubScanArea(
            this.scanningArea,
            this.measurementAcquisitionService.activeSetup.sensorPositions.map(
                (x) => <Point3D>{ x: x.X, y: x.Y, z: x.Z }
            ),
            this.resolution,
            this.channelSelection
        );

        if (
            subScanArea &&
            subScanArea.horizontalStep !== this.subScanArea.horizontalStep &&
            subScanArea.verticalStep !== this.subScanArea.verticalStep &&
            subScanArea.numberOfHorizontalSteps !== this.subScanArea.numberOfHorizontalSteps &&
            subScanArea.numberOfVerticalSteps !== this.subScanArea.numberOfVerticalSteps
        ) {
            this.subScanArea = this.subScanArea;
        }
    }

    private setMeasurementAreaPosition(reset?: boolean): void {
        if (reset) {
            this.areaOffsetX = 0;
            this.areaOffsetY = 0;
            this.areaOffsetZ = 0;
        } else {
            this.areaOffsetX = this.motionService.lastKnownPos.x;
            this.areaOffsetY = this.motionService.lastKnownPos.y;
            this.areaOffsetZ = this.motionService.lastKnownPos.z;
        }

        this.measurementAreaChanged();
    }
    //#endregion
}
